/**
 * Announces a message to screen readers via a persistent aria-live region
 * that lives outside React's component tree, so it is never destroyed by
 * component re-renders or unmounts.
 *
 * Usage:
 *   import announce from '@Utils/announce';
 *   announce('Page 3 of 10');
 */

const ANNOUNCER_ID = 'app-aria-live-announcer';
let announcer = null;

function getAnnouncer() {
  // If we already have a cached announcer, and it's still in the DOM, reuse it.
  if (announcer && document.body.contains(announcer)) {
    return announcer;
  }

  // Try to find an existing announcer element by id. This makes the announcer
  // resilient to module reloads (e.g., Vite HMR) where the DOM persists but
  // module-scoped state is reset.
  let el = document.getElementById(ANNOUNCER_ID);

  if (!el) {
    el = document.createElement('div');
    el.setAttribute('id', ANNOUNCER_ID);
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    // Visually hidden but still readable by screen readers
    el.setAttribute('class', 'sr-only');
    document.body.appendChild(el);
  }

  announcer = el;
  return announcer;
}

export default function announce(message) {
  const el = getAnnouncer();
  // Clear first so screen readers detect a fresh mutation even if the
  // message text is identical to the previous announcement.
  el.textContent = '';
  setTimeout(() => {
    el.textContent = message;
  }, 100);
}
